import { Button, Input as DSInput } from 'design-system';
import { ChevronLeft, X as CloseOutlined, Search as SearchOutlined } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { Metallic as MetallicIcon } from '../../design-system/icons';
import { trackEvent } from '../../analytics/consent';
import {
  clearRecent,
  getRecent,
  removeRecent,
  saveRecent,
} from '../../features/search/recentSearch';
import { buildUnifiedSearchPath } from '../../features/search/searchRoutes';
import { useUnifiedSearch } from '../../features/search/useUnifiedSearch';
import useIsMobile from '../../hooks/useIsMobile';
import SearchPanel from './SearchPanel';
import './search.css';

export default function HeaderSearch({ fetcher, hideOnPaths = ['/search'], onOpenChange }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isHidden = hideOnPaths.some((p) => pathname.startsWith(p));
  const intl = useIntl();
  const isMobile = useIsMobile();
  const [themeState, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('theme');
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateTheme = () => {
      const stored = localStorage.getItem('theme');
      setThemeState(stored);
    };
    updateTheme();

    const handleStorage = (event) => {
      if (!event || event.key === 'theme') updateTheme();
    };

    window.addEventListener('storage', handleStorage);

    const observer = new MutationObserver(() => updateTheme());
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => {
      window.removeEventListener('storage', handleStorage);
      observer.disconnect();
    };
  }, []);

  const themeFromStorage = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
  const theme = themeFromStorage ?? themeState;
  const isDark = theme === 'dark';

  const renderSearchIcon = useCallback(
    (iconProps = {}) =>
      isDark ? (
        <MetallicIcon Icon={SearchOutlined} variant="silver" {...iconProps} />
      ) : (
        <SearchOutlined {...iconProps} />
      ),
    [isDark],
  );

  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(getRecent());
  const rootRef = useRef(null);

  const { query, setQuery, results, loading } = useUnifiedSearch(fetcher, {
    limit: 5,
    debounce: 1000,
  });

  const hasQuery = query.trim().length > 0;

  const setOpenState = (next) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const openSearch = () => {
    if (!open) setOpenState(true);
  };

  const closeSearch = () => {
    if (open) setOpenState(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpenState(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      const inText = e.target?.closest?.('input, textarea, [contenteditable]');
      const modK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if (!inText && (e.key === '/' || modK)) {
        e.preventDefault();
        setOpenState(true);
        setTimeout(() => rootRef.current?.querySelector('input')?.focus(), 0);
      }
      if (e.key === 'Escape') {
        setOpenState(false);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (isHidden) return null;

  const handleSubmit = (value) => {
    const term = value.trim();
    if (!term) return;

    saveRecent(term);
    setRecent(getRecent());
    closeSearch();
    trackEvent('search_performed', {
      query_length: term.length,
      source: 'header',
      results_count: results?.length || 0,
    });

    nav(buildUnifiedSearchPath(term));
  };

  const handlePick = (item) => {
    if (item.href) {
      saveRecent(item.title || query, item.type);
      setRecent(getRecent());
      closeSearch();
      const idx = results.findIndex((r) => r && r.href === item.href);
      trackEvent('search_result_clicked', {
        position: idx >= 0 ? idx + 1 : null,
        grave_id: item.type === 'grave' ? item.id || item.grave_id || null : null,
        member_id: item.type === 'member' ? item.id || null : null,
        source: 'header',
      });
      nav(item.href);
      return;
    }
    if (item.q) {
      handleSubmit(item.q);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`hdr-search ${open ? 'open' : 'collapsed'}`}
      style={{
        width: open ? '100%' : 40,
        maxWidth: '100%',
      }}
    >
      {!open && (
        <Button
          aria-label={intl.formatMessage({ id: 'search_open', defaultMessage: 'Open search' })}
          onClick={() => {
            openSearch();
            setTimeout(() => rootRef.current?.querySelector('input')?.focus(), 0);
          }}
          size="small"
          variant="filled"
          shape="circle"
          color="var(--surface-3)"
          style={{ color: 'var(--text)' }}
          icon={renderSearchIcon({ size: 18, 'aria-hidden': true })}
        />
      )}
      {open && (
        <form
          className="hdr-search__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(query);
          }}
        >
          <div className="hdr-search__field">
            <Button
              type="submit"
              size="small"
              variant="text"
              shape="circle"
              className="hdr-search__fieldBtn"
              aria-label={intl.formatMessage({ id: 'search_submit', defaultMessage: 'Search' })}
            >
              {renderSearchIcon({ size: 18, 'aria-hidden': true })}
            </Button>
            <DSInput
              autoFocus
              variant="borderless"
              className="hdr-search__input"
              placeholder={intl.formatMessage({
                id: 'search_all_placeholder',
                defaultMessage: 'Search for people and graves',
              })}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {hasQuery && (
              <Button
                type="button"
                className="hdr-search__clear"
                onClick={() => setQuery('')}
                aria-label={intl.formatMessage({
                  id: 'clear_search',
                  defaultMessage: 'Clear search',
                })}
                shape="circle"
                variant="text"
                size="small"
              >
                <CloseOutlined size={18} />
              </Button>
            )}
            {isMobile && (
              <Button
                type="button"
                className="hdr-search__close"
                onClick={closeSearch}
                aria-label={intl.formatMessage({
                  id: 'close_search',
                  defaultMessage: 'Close search',
                })}
                shape="circle"
                variant="text"
                size="small"
              >
                <ChevronLeft size={18} />
              </Button>
            )}
          </div>
        </form>
      )}

      {open && (hasQuery || recent.length > 0) && (
        <SearchPanel
          query={query}
          loading={loading}
          results={results}
          recent={recent}
          hasQuery={hasQuery}
          onPick={handlePick}
          onRemoveRecent={(q, type) => {
            removeRecent(q, type);
            setRecent(getRecent());
          }}
          onClearRecent={() => {
            clearRecent();
            setRecent([]);
          }}
          onOpenFull={() => handleSubmit(query)}
        />
      )}
    </div>
  );
}
